import { useState } from "react";
import { RefreshCw, Check } from "lucide-react";
import { useEffect } from "react";
import { fetchHoroscope } from "../../services/horosocope";
import { successToast, errorToast } from "../../utils/toasters";
import { updateHoroscopePreference } from "../../services/profile";
import { getUserId, isAuthenticated } from "../../utils/helpers";
import GenericCardSkeleton from "../skeletons/GenericCardSkeleton";

// Zodiac signs data
const zodiacSigns = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
];

const styles = `
  @keyframes pulse-subtle {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.02);
    }
  }
  
  .animate-pulse-subtle {
    animation: pulse-subtle 2s ease-in-out infinite;
  }
  
  @keyframes slide-from-left {
    from {
      opacity: 0;
      transform: translateX(-60px) scale(0.6);
    }
    to {
      opacity: 0.25;
      transform: translateX(0) scale(0.75);
    }
  }
  
  @keyframes slide-from-right {
    from {
      opacity: 0;
      transform: translateX(60px) scale(0.6);
    }
    to {
      opacity: 0.25;
      transform: translateX(0) scale(0.75);
    }
  }
  
  @keyframes slide-to-center {
    from {
      opacity: 0.25;
      transform: scale(0.75);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @keyframes slide-to-left {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0.25;
      transform: translateX(-20px) scale(0.75);
    }
  }
  
  @keyframes slide-to-right {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0.25;
      transform: translateX(20px) scale(0.75);
    }
  }
  
  .slide-from-left {
    animation: slide-from-left 0.6s ease-out forwards;
  }
  
  .slide-from-right {
    animation: slide-from-right 0.6s ease-out forwards;
  }
  
  .slide-to-center {
    animation: slide-to-center 0.6s ease-out forwards;
  }
  
  .slide-to-left {
    animation: slide-to-left 0.6s ease-out forwards;
  }
  
  .slide-to-right {
    animation: slide-to-right 0.6s ease-out forwards;
  }
`;

export default function HoroscopeCard() {
  const dayIcon = "/images/sun-icon.png";
  const nightIcon = "/images/moon-icon.png";

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [prevSelectedIndex, setPrevSelectedIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("sun");
  const [isChanging, setIsChanging] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [horoscopeData, setHoroscopeData] = useState([]);
  const [animationKey, setAnimationKey] = useState(0);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState(false);
  const [isProcessing, setIsProcessing] = useState(true);

  const currentZodiac = zodiacSigns[currentIndex];

  const handleTabChange = async (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
      
      if (isAuthenticated()) {
        try {
          const userId = getUserId();
          if (userId) {
            setIsUpdatingPreference(true);
            await updateHoroscopePreference(
              userId,
              tab,
              zodiacSigns[currentIndex]
            );
          }
        } catch (error) {
          errorToast("Failed to update horoscope preference");
        } finally {
          setIsUpdatingPreference(false);
        }
      }
    }
  };

  useEffect(() => {
    async function fetchHoroscopes() {
      try {
        setIsProcessing(true);
        const payload = {
          mode: activeTab,
          sign: zodiacSigns[currentIndex],
        };
        const horoscopeResponse = await fetchHoroscope(payload);
        setHoroscopeData(horoscopeResponse.horoscope);
      } catch (error) {
        errorToast("Error while fetching the horoscope");
      } finally {
        setIsProcessing(false);
      }
    }
    fetchHoroscopes();
  }, [activeTab, currentIndex]);

  const handleChange = () => {
    if (isChanging) return;
    setIsSelectionMode(true);
    setSelectedIndex(currentIndex);
    setPrevSelectedIndex(currentIndex);
  };

  const handleDone = async () => {
    setIsChanging(true);
    setTimeout(async () => {
      setCurrentIndex(selectedIndex);
      setIsSelectionMode(false);
      setIsChanging(false);
      
      if (isAuthenticated()) {
        try {
          const userId = getUserId();
          if (userId) {
            setIsUpdatingPreference(true);
            await updateHoroscopePreference(
              userId,
              activeTab,
              zodiacSigns[selectedIndex]
            );
          }
        } catch (error) {
          errorToast("Failed to update horoscope preference");
        } finally {
          setIsUpdatingPreference(false);
        }
      }
    }, 300);
  };

  const handleZodiacSelect = (index) => {
    setSelectedIndex(index);
    handleDone();
  };

  const handleNavigation = (direction) => {
    setPrevSelectedIndex(selectedIndex);
    if (direction === "prev") {
      setSelectedIndex(
        selectedIndex === 0 ? zodiacSigns.length - 1 : selectedIndex - 1
      );
    } else {
      setSelectedIndex((selectedIndex + 1) % zodiacSigns.length);
    }
    setAnimationKey((prev) => prev + 1);
  };

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  const prevIndex =
    selectedIndex === 0 ? zodiacSigns.length - 1 : selectedIndex - 1;
  const nextIndex = (selectedIndex + 1) % zodiacSigns.length;

  if (isProcessing) {
    return <GenericCardSkeleton />;
  }

  return (
    <div className="w-full font-afacad">
      <div className="rounded-xl overflow-hidden shadow-md bg-white">
        <div className="h-36 relative flex items-end overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <img
              src={
                activeTab === "sun"
                  ? "/images/zodiac-moon.png"
                  : "/images/zodiac-sun.png"
              }
              alt="Zodiac background"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="flex w-full h-full">
            <button
              className={`flex-1 flex flex-col items-center justify-center pb-2 relative ${
                activeTab === "sun"
                  ? "text-[#7B189F] font-medium"
                  : "text-gray-500"
              }`}
              onClick={() => handleTabChange("sun")}
            >
              <span className="text-yellow-400 text-5xl mb-2">
                <img
                  src={dayIcon}
                  alt="Day icon"
                  className="h-full object-contain rounded-full bg-white/10 backdrop-blur-sm"
                />
              </span>
              <div
                className={`${
                  activeTab === "sun" ? "text-black" : "text-gray-500"
                } font-afacad font-semibold`}
              >
                Sun Sign
              </div>
              {activeTab === "sun" && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-[#7B189F]" />
              )}
            </button>

            <button
              className={`flex-1 flex flex-col items-center justify-center pb-2 relative ${
                activeTab === "moon"
                  ? "text-[#7B189F] font-medium"
                  : "text-gray-500"
              }`}
              onClick={() => handleTabChange("moon")}
            >
              <span className="text-yellow-300 text-5xl mb-2">
                <img
                  src={nightIcon}
                  alt="Night icon"
                  className={`h-full object-contain rounded-full bg-white/10 backdrop-blur-sm ${
                    activeTab === "moon" ? "border-[#7B189F]" : ""
                  }`}
                />
              </span>
              <div
                className={`${
                  activeTab === "moon" ? "text-black" : "text-gray-500"
                } font-afacad font-semibold`}
              >
                Moon Sign
              </div>
              {activeTab === "moon" && (
                <div className="absolute bottom-0 left-0 w-full h-1 bg-[#7B189F]" />
              )}
            </button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-12 h-12 rounded-full border-2 border-purple-500 flex items-center justify-center bg-white overflow-hidden p-1">
                <img
                  src={`/images/${currentZodiac.toLowerCase()}.png`}
                  alt="Zodiac Sign"
                />
              </div>

              <div className="ml-3">
                <div className="flex items-center">
                  <span
                    className={`${
                      activeTab === "sun"
                        ? "text-yellow-500"
                        : "text-yellow-300"
                    } mr-2 text-lg`}
                  >
                    {activeTab === "sun" ? "☀️" : "🌙"}
                  </span>
                  <span className="text-xl font-medium text-gray-800">
                    {currentZodiac}
                  </span>
                </div>
                <button
                  className="text-xs text-purple-500 hover:text-purple-700"
                  onClick={() =>
                    handleTabChange(activeTab === "sun" ? "moon" : "sun")
                  }
                >
                  Switch to {activeTab === "sun" ? "Moon" : "Sun"} Sign
                </button>
              </div>
            </div>

            <button
              className="flex items-center text-purple-500 hover:text-purple-700 transition-colors"
              onClick={isSelectionMode ? handleDone : handleChange}
              disabled={isChanging}
            >
              <span className="mr-1">
                {isSelectionMode ? "Done" : "Change"}
              </span>
              {isSelectionMode ? (
                <Check className="h-4 w-4" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </button>
          </div>

          <div className="mt-4">
            {!isSelectionMode ? (
              <>
                <div className="flex items-center mb-2">
                  <span className="text-lg mr-2">
                    {activeTab === "sun" ? "✨" : "🌙"}
                  </span>
                  <span className="font-bold text-gray-700">
                    {activeTab === "sun" ? "Sun Vibes:" : "Moon Vibes:"}
                  </span>
                </div>

                <div
                  className={`text-gray-600 text-sm mt-1 ${
                    isChanging ? "opacity-0" : "opacity-100"
                  }`}
                >
                  {horoscopeData && horoscopeData.witty_message}
                </div>
              </>
            ) : (
              <div className="transition-all duration-300 ease-in-out">
                <div className="flex items-center mb-6">
                  <span className="text-lg mr-2">🔮</span>
                  <span className="font-medium text-gray-700">
                    Choose Your Sign:
                  </span>
                </div>

                <div className="relative w-full h-32 flex items-center justify-center overflow-hidden">
                  <div
                    className="flex items-center justify-center space-x-6 w-full"
                    key={animationKey}
                  >
                    <div className="relative">
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-full"></div>
                      <button
                        onClick={() => handleNavigation("prev")}
                        className="relative w-16 h-16 rounded-full border border-purple-100 flex items-center justify-center bg-white/80 backdrop-blur-sm overflow-hidden p-2 transition-all duration-500 opacity-25 hover:opacity-40 scale-75 hover:scale-80 transform shadow-sm"
                      >
                        <img
                          src={`/images/${zodiacSigns[
                            prevIndex
                          ].toLowerCase()}.png`}
                          alt={zodiacSigns[prevIndex]}
                          className="w-full h-full object-contain transition-transform duration-300"
                        />
                      </button>
                    </div>

                    <div className="relative z-10">
                      <button
                        onClick={() => handleZodiacSelect(selectedIndex)}
                        className="w-24 h-24 rounded-full border-2 border-purple-500 flex items-center justify-center bg-white overflow-hidden p-3 transition-all duration-500 hover:scale-110 shadow-sm ring-2 ring-purple-100 hover:ring-4 hover:ring-purple-200 transform animate-pulse-subtle"
                      >
                        <img
                          src={`/images/${zodiacSigns[
                            selectedIndex
                          ].toLowerCase()}.png`}
                          alt={zodiacSigns[selectedIndex]}
                          className="w-full h-full object-contain transition-all duration-300 hover:rotate-12"
                        />
                      </button>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 bg-white/60 backdrop-blur-sm rounded-full"></div>
                      <button
                        onClick={() => handleNavigation("next")}
                        className="relative w-16 h-16 rounded-full border border-purple-100 flex items-center justify-center bg-white/80 backdrop-blur-sm overflow-hidden p-2 transition-all duration-500 opacity-25 hover:opacity-40 scale-75 hover:scale-80 transform shadow-sm"
                      >
                        <img
                          src={`/images/${zodiacSigns[
                            nextIndex
                          ].toLowerCase()}.png`}
                          alt={zodiacSigns[nextIndex]}
                          className="w-full h-full object-contain transition-transform duration-300"
                        />
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => handleNavigation("prev")}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm border border-purple-300 flex items-center justify-center text-purple-500 hover:bg-purple-50 hover:border-purple-400 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-110 active:scale-95"
                  >
                    <svg
                      className="w-4 h-4 transition-transform duration-200 hover:-translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={() => handleNavigation("next")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm border border-purple-300 flex items-center justify-center text-purple-500 hover:bg-purple-50 hover:border-purple-400 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-110 active:scale-95"
                  >
                    <svg
                      className="w-4 h-4 transition-transform duration-200 hover:translate-x-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                <div className="text-center mt-4">
                  <p className="text-lg font-semibold text-gray-800 transition-all duration-500 transform hover:scale-105">
                    {zodiacSigns[selectedIndex]}
                  </p>
                  <p className="text-xs text-purple-600 mt-1 transition-opacity duration-300 hover:opacity-80">
                    Tap to select or use arrows to browse
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
